import React, { useCallback, useRef, useState } from 'react';
import { track } from '@/lib/analytics';
import {
  Upload,
  FileImage,
  Loader2,
  Check,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Coins,
  FileText,
  RotateCcw,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FloorplanState } from '@/types';
import { isPdfFile, convertPdfToImage } from '@/services/pdfConversion';
import { uploadFloorplan } from '@/services/floorplanService';
import { FloorplanStepIndicator } from '@/components/FloorplanStepIndicator';

interface FloorplanUploadViewProps {
  projectId: string;
  projectName?: string;
  floorplanState: FloorplanState;
  onFloorplanStateUpdate: (updates: Partial<FloorplanState>) => void;
  onNext: () => void;
  onBack: () => void;
  credits: number;
  floorplanUnits: number;
  onBuyCredits: () => void;
  onContinueExisting: () => void;
  onStartOver: () => void | Promise<void>;
}

type UploadStatus = 'idle' | 'converting' | 'uploading' | 'done' | 'error';

export const FloorplanUploadView: React.FC<FloorplanUploadViewProps> = ({
  projectId,
  projectName,
  floorplanState,
  onFloorplanStateUpdate,
  onNext,
  onBack,
  floorplanUnits,
  onBuyCredits,
  onContinueExisting,
  onStartOver,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>(
    floorplanState.previewUrl ? 'done' : 'idle'
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = useCallback(
    async (file: File) => {
      setErrorMessage(null);

      try {
        // PDF conversion
        let imageFile = file;
        if (isPdfFile(file)) {
          setUploadStatus('converting');
          imageFile = await convertPdfToImage(file);
        }

        // Upload to Supabase
        setUploadStatus('uploading');
        const result = await uploadFloorplan(projectId, imageFile);
        track('Floorplan Uploaded');

        onFloorplanStateUpdate({
          imageDbId: result.dbId,
          originalStoragePath: result.storagePath,
          previewUrl: result.previewUrl,
          // Clear old generation results so the editor doesn't auto-skip to review
          generated2dUrl: null,
          generated2dPath: null,
          generated3dUrl: null,
          generated3dPath: null,
        });

        setUploadStatus('done');
      } catch (err) {
        console.error('[FloorplanUpload] Error:', err);
        setUploadStatus('error');
        setErrorMessage(
          err instanceof Error ? err.message : 'Upload fehlgeschlagen'
        );
      }
    },
    [projectId, onFloorplanStateUpdate]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      // Reset input
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [handleFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleReplace = useCallback(() => {
    onFloorplanStateUpdate({
      imageDbId: null,
      originalStoragePath: null,
      previewUrl: null,
      generated2dUrl: null,
      generated2dPath: null,
      generated3dUrl: null,
      generated3dPath: null,
    });
    setUploadStatus('idle');
    setErrorMessage(null);
  }, [onFloorplanStateUpdate]);

  const hasUpload = uploadStatus === 'done' && floorplanState.previewUrl;
  // User has committed progress beyond a fresh upload (extracted unit or generated 2D/3D)
  const hasExistingProgress = !!(
    floorplanState.extractedUnitUrl ||
    floorplanState.generated2dUrl ||
    floorplanState.generated3dUrl
  );

  const existingStatusLabel = (() => {
    if (floorplanState.branded2dUrl) return '2D gespeichert mit Branding';
    if (floorplanState.generated3dUrl) return '2D + 3D generiert — Branding noch offen';
    if (floorplanState.generated2dUrl) return '2D generiert — 3D noch offen';
    if (floorplanState.extractedUnitUrl) return 'Grundriss markiert — noch nicht generiert';
    return 'Grundriss hochgeladen';
  })();

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              Grundriss-Optimierung
            </h1>
            {projectName && (
              <p className="text-sm text-muted-foreground">{projectName}</p>
            )}
          </div>
        </div>
        <Badge variant="outline" className="gap-1.5">
          <Coins className="w-3.5 h-3.5" />
          {floorplanUnits} Grundriss{floorplanUnits !== 1 ? 'e' : ''}
        </Badge>
      </div>

      <FloorplanStepIndicator currentStep="upload" />

      {/* Upload Area */}
      <Card>
        <CardContent className="p-6">
          {!hasUpload ? (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() =>
                uploadStatus !== 'uploading' &&
                uploadStatus !== 'converting' &&
                fileInputRef.current?.click()
              }
              className={`
                relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-12
                transition-all cursor-pointer
                ${
                  isDragging
                    ? 'border-primary bg-primary/5 scale-[1.01]'
                    : uploadStatus === 'error'
                      ? 'border-destructive/50 bg-destructive/5'
                      : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50'
                }
              `}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                className="hidden"
                onChange={handleInputChange}
              />

              {uploadStatus === 'converting' && (
                <>
                  <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
                  <p className="text-lg font-medium">PDF wird konvertiert...</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Seite 1 wird als Bild extrahiert
                  </p>
                </>
              )}

              {uploadStatus === 'uploading' && (
                <>
                  <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
                  <p className="text-lg font-medium">Wird hochgeladen...</p>
                </>
              )}

              {uploadStatus === 'error' && (
                <>
                  <AlertCircle className="w-12 h-12 text-destructive mb-4" />
                  <p className="text-lg font-medium text-destructive">
                    Upload fehlgeschlagen
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {errorMessage || 'Bitte versuchen Sie es erneut'}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={(e) => {
                      e.stopPropagation();
                      setUploadStatus('idle');
                      setErrorMessage(null);
                    }}
                  >
                    Erneut versuchen
                  </Button>
                </>
              )}

              {uploadStatus === 'idle' && (
                <>
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                    <Upload className="w-8 h-8 text-primary" />
                  </div>
                  <p className="text-lg font-medium">
                    Grundriss hier ablegen oder klicken
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    JPG, PNG oder PDF
                  </p>
                  <div className="flex items-center gap-4 mt-4">
                    <Badge variant="secondary" className="gap-1.5">
                      <FileImage className="w-3.5 h-3.5" />
                      JPG / PNG
                    </Badge>
                    <Badge variant="secondary" className="gap-1.5">
                      <FileText className="w-3.5 h-3.5" />
                      PDF (Seite 1)
                    </Badge>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Preview */}
              <div className="relative rounded-xl overflow-hidden border bg-muted/30">
                <img
                  src={floorplanState.previewUrl!}
                  alt="Grundriss Vorschau"
                  className="w-full max-h-[500px] object-contain"
                />
                <div className="absolute top-3 right-3">
                  <Badge className="gap-1.5 bg-emerald-600 hover:bg-emerald-700">
                    <Check className="w-3.5 h-3.5" />
                    Hochgeladen
                  </Badge>
                </div>
              </div>

              {/* Status + Resume/Reset Buttons (only when user has committed progress) */}
              {hasExistingProgress ? (
                <div className="space-y-3">
                  <div className="text-center">
                    <p className="text-sm font-medium text-foreground">Bestehender Grundriss gefunden</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{existingStatusLabel}</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button onClick={onContinueExisting} size="lg" className="flex-1 gap-2">
                      Weiterarbeiten
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" onClick={onStartOver} size="lg" className="flex-1 gap-2">
                      <RotateCcw className="w-4 h-4" />
                      Neuen Grundriss hochladen
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-center">
                  <Button variant="outline" size="sm" onClick={handleReplace}>
                    Anderen Grundriss wählen
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-muted/30 border-dashed">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <FileImage className="w-4 h-4 text-primary" />
            </div>
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">
                Tipps für optimale Ergebnisse
              </p>
              <ul className="space-y-1">
                <li>
                  Verwenden Sie einen gut lesbaren, technischen 2D-Grundriss
                </li>
                <li>Stellen Sie sicher, dass Wände und Räume klar erkennbar sind</li>
                <li>
                  Bei PDF-Dateien wird automatisch die erste Seite verwendet
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>

        <div className="flex items-center gap-3">
          {floorplanUnits < 1 && (
            <Button variant="outline" size="sm" onClick={onBuyCredits}>
              <Coins className="w-4 h-4 mr-2" />
              Grundriss-Kontingent kaufen
            </Button>
          )}
          <Button
            onClick={onNext}
            disabled={!hasUpload}
            className="gap-2"
          >
            Weiter zur Konfiguration
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
